import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { getPhase, votingDeadlineDate, DEFAULT_SETTINGS } from "@/lib/phase";

export default function ReminderBanner({ externalVotingUrl, settings = DEFAULT_SETTINGS }) {
  const phase = getPhase(settings);
  const [dismissed, setDismissed] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("reminder-dismissed") === "1") setDismissed(true);
  }, []);

  const dismiss = () => {
    setDismissed(true);
    sessionStorage.setItem("reminder-dismissed", "1");
  };

  if (dismissed) return null;
  if (phase > 3) return null;

  const deadline = votingDeadlineDate(settings);
  const ms = deadline.getTime() - Date.now();
  const days = Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));

  const message =
    phase === 3
      ? `Voting closes in ${days} day${days === 1 ? "" : "s"}. Every $2 vote moves a business forward.`
      : phase === 2
      ? "Pitches are live this week. Watch and vote — every $2 counts."
      : `Community voting is open. Cast your $2 vote before June 22.`;

  const handleRemind = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/contacts/tag", { email, tags: ["Community Voter", "Pitch Viewer"] });
      setSubmitted(true);
    } catch {}
    setSubmitting(false);
  };

  return (
    <div data-testid="reminder-banner" className="sticky top-0 z-40 bg-emerald-950 text-white border-b border-emerald-800">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-3 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <span className="h-2 w-2 rounded-full bg-amber-300" />
          <p className="text-sm font-[Outfit] text-white">{message}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {phase === 3 && !submitted && (
            <form onSubmit={handleRemind} className="flex items-center gap-2">
              <input
                type="email"
                required
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="remind-email-input"
                className="bg-emerald-900 border border-emerald-700 rounded-full px-3 py-1.5 text-sm placeholder:text-emerald-200 text-white focus:outline-none focus:ring-2 focus:ring-amber-300 font-[Outfit]"
              />
              <Button
                type="submit"
                data-testid="remind-btn"
                disabled={submitting}
                size="sm"
                className="rounded-full bg-amber-300 hover:bg-amber-400 text-emerald-950 font-[Outfit] font-semibold"
              >
                {submitting ? "…" : "Remind me daily"}
              </Button>
            </form>
          )}
          {submitted && <span className="text-xs text-amber-300 font-[Outfit] font-semibold">Got it — we'll remind you.</span>}
          <Button asChild size="sm" data-testid="banner-vote-btn" className="rounded-full bg-rose-500 hover:bg-rose-600 text-white font-[Outfit] font-semibold">
            <a href={externalVotingUrl} target="_blank" rel="noreferrer">Vote · $2</a>
          </Button>
          <button onClick={dismiss} data-testid="banner-dismiss" aria-label="Dismiss" className="text-white hover:text-amber-300 text-xl leading-none">×</button>
        </div>
      </div>
    </div>
  );
}
